/*
  main-product.js

  Enhancement only. Everything this file does, the page already does without it:

    - The variant picker is name="id" radios (or a native <select name="id">),
      so the browser posts a real variant to /cart/add with no script involved.
    - The refill plan panel is shown and hidden by CSS off the bundle radio's
      :checked state, not from here.
    - With this file blocked, .no-js hides the offer picker and the page is a
      plain Shopify product form that adds the device.

  What it adds:
    1. Variant change updates the price, the button, the URL, and the gallery.
    2. Submit becomes one /cart/add.js call carrying BOTH line items when the
       bundle is selected, with the refill's selling_plan attached.

  It never computes a discount. The bundle discount is an automatic discount in
  the admin (CLAUDE.md section 3) and the authoritative totals are whatever the
  cart returns.

  ── The add asks for the cart surfaces in the SAME request ──────────────────
  Adding used to be: POST the add, then fetch /cart.js, then fire an event, and
  leave the cart drawer to go and ask the server for its own new markup in a
  SECOND round trip. That second trip is where the drawer's contents came from,
  and every one of its failure modes was silent — so the drawer opened on the
  markup it had been rendered with at page load, and nothing ever said why.

  So the add now carries `sections`, and its response comes back with the
  re-rendered Liquid for every cart surface on the page. That is the same rule
  cart.js already states for itself: state derives from the response to the
  request that changed the cart, never from a later request that has to be
  trusted to land.

  The event still fires even when the follow-up /cart.js read fails, because the
  add itself has already succeeded by then and a drawer that never hears about a
  committed add is the worse of the two failures.

  ── Why one IIFE ───────────────────────────────────────────────────────────
  The theme editor re-inserts a section's <script> when the section re-renders,
  and a re-inserted script element executes again. Top-level `class` and `const`
  are lexical declarations, so a second execution in the shared global scope is
  a hard SyntaxError rather than a subtle bug. Same reasoning, and the same
  guarded define, as cart.js.

  Diagnostics: append ?rns-debug to any URL and this file narrates the whole
  path — what it submitted, what came back, the item_count it read, and the
  event it fired. Every failure says so instead of failing quietly.
*/

(function () {
  'use strict';

  const DEBUG = window.location.search.indexOf('rns-debug') !== -1;
  const log = function () {
    if (!DEBUG) return;
    const args = Array.prototype.slice.call(arguments);
    console.log.apply(console, ['[rns-product]'].concat(args));
  };

  const root = () =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /*
    Every cart surface currently on the page, by section id. The drawer is one;
    on /cart the page's own cart is another. Asking for them by id rather than
    naming the drawer means this file needs to know nothing about which cart
    sections a template happens to stack — a template that adds a third gets it
    for free, and a page with none simply sends no `sections` at all.
  */
  const cartSectionIds = () => {
    const ids = [];
    document.querySelectorAll('rns-cart[data-rns-section-id]').forEach((host) => {
      const id = host.dataset.rnsSectionId;
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    // The Section Rendering API accepts at most five.
    return ids.slice(0, 5);
  };

  class RnsProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.offers = this.querySelector('[data-rns-offers]');
      this.status = this.querySelector('[data-rns-status]');
      this.submit = this.querySelector('[data-rns-submit]');
      this.submitText = this.querySelector('[data-rns-submit-text]');
      this.priceTarget = this.querySelector('.rns-main-product__price');
      this.savingTarget = this.querySelector('[data-rns-offer-savings]');
      this.track = this.querySelector('[data-rns-media-track]');
      this.variants = this.readVariants();

      this.sticky = this.querySelector('[data-rns-sticky-atc]');
      this.stickySubmit = this.querySelector('[data-rns-sticky-submit]');
      this.stickySubmitText = this.querySelector('[data-rns-sticky-submit-text]');
      this.stickyOption = this.querySelector('[data-rns-sticky-option]');
      this.stickyPrice = this.querySelector('.rns-sticky-atc__price');

      this.addEventListener('change', this.onChange.bind(this));
      this.watchBuyBox();

      // Only take over submission when there is a second line item to add. A
      // plain product form is better left native: fewer moving parts on the one
      // interaction the business depends on.
      if (this.offers) {
        this.form.addEventListener('submit', this.onSubmit.bind(this));
        log('ready: offer picker present, so submit is intercepted');
      } else {
        log('ready: no offer picker, so submit stays native (full page POST)');
      }
    }

    // The theme editor removes and re-inserts a section on every edit. Without
    // this, each edit leaves another live observer holding a detached button.
    disconnectedCallback() {
      if (this.stickyObserver) {
        this.stickyObserver.disconnect();
        this.stickyObserver = null;
      }
    }

    readVariants() {
      const node = this.querySelector('[data-rns-variants]');
      if (!node) return [];
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        return [];
      }
    }

    currentVariantId() {
      const field = this.form.querySelector('[name="id"]:checked, select[name="id"], input[type="hidden"][name="id"]');
      return field ? field.value : null;
    }

    onChange(event) {
      if (!event.target.name || event.target.name !== 'id') return;

      const id = this.currentVariantId();
      const variant = this.variants.find((item) => String(item.id) === String(id));
      if (!variant) return;

      if (this.priceTarget && variant.price_html) {
        this.priceTarget.outerHTML = variant.price_html;
        this.priceTarget = this.querySelector('.rns-main-product__price');
      }

      this.setAvailability(variant.available);
      this.showMedia(variant.media_id);
      this.syncSticky(variant);
      this.syncBundleSaving(variant);

      // Keeps the URL shareable and the back button honest. replaceState rather
      // than pushState so choosing a color does not fill the history stack.
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      } catch (error) {
        /* no-op */
      }
    }

    setAvailability(available) {
      const label = available ? this.dataset.rnsAddToCart : this.dataset.rnsSoldOut;

      if (this.submit) {
        this.submit.disabled = !available;
        if (this.submitText) this.submitText.textContent = label;
      }

      // The sticky bar carries the same state. A bar that still offered "Add to
      // cart" for a colour that is sold out would be the toggle-that-lies
      // failure in section 7, on the control furthest from the evidence.
      if (this.stickySubmit) {
        this.stickySubmit.disabled = !available;
        if (this.stickySubmitText) this.stickySubmitText.textContent = label;
      }
    }

    /*
      Keep the sticky bar describing the variant the customer actually has
      selected. The price is not re-formatted here: it is lifted out of the same
      pre-rendered price_html the buy box uses, so money formatting stays in
      Liquid where the store's currency format lives, and there is exactly one
      place a price can be wrong.
    */
    /*
      The bundle badge states a cash amount taken off this variant's price, so
      it has to move when the variant does. The string is pre-rendered per
      variant in Liquid alongside price_html — same reason: the money format and
      the rounding direction stay in one place, and nothing here recomputes a
      figure a customer reads.

      The handle only exists in derived mode, so in the merchant-typed fallback
      this finds no target and leaves the typed value alone. A variant with no
      derivable amount hides the badge rather than showing an empty pill.
    */
    syncBundleSaving(variant) {
      if (!this.savingTarget) return;
      const text = variant.bundle_saving || '';
      this.savingTarget.textContent = text;
      this.savingTarget.hidden = text === '';
    }

    syncSticky(variant) {
      if (this.stickyOption && variant.title) this.stickyOption.textContent = variant.title;

      if (this.stickyPrice && variant.price_html) {
        const holder = document.createElement('div');
        holder.innerHTML = variant.price_html;
        const rendered = holder.firstElementChild;
        if (rendered) this.stickyPrice.innerHTML = rendered.innerHTML;
      }
    }

    /*
      Reveal the sticky bar once the buy box has left the viewport.

      Both halves of the test are needed. `!isIntersecting` alone is also true
      while the visitor is ABOVE the buy box — which a restored scroll position,
      a back/forward navigation or an anchor link all produce — and the bar
      would appear over a hero the customer has not scrolled past yet. The
      second half, boundingClientRect.top < 0, is what distinguishes "scrolled
      past it" from "not reached it".

      With no IntersectionObserver the bar simply stays hidden. It duplicates a
      button already on the page, so that costs the customer nothing.
    */
    watchBuyBox() {
      if (!this.sticky) return;

      if (!('IntersectionObserver' in window)) {
        log('sticky bar: no IntersectionObserver, so it stays hidden.');
        return;
      }

      const anchor = this.submit || this.form;
      if (!anchor) return;

      this.stickyObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.setStickyVisible(past);
          });
        },
        { threshold: 0 }
      );

      this.stickyObserver.observe(anchor);
      log('sticky bar: watching', anchor.tagName.toLowerCase(), 'for the buy box leaving the viewport');
    }

    setStickyVisible(visible) {
      if (!this.sticky || this.stickyVisible === visible) return;
      this.stickyVisible = visible;

      if (visible) {
        // Two steps, because a transition cannot run from display:none. The
        // element is put in flow first, then the entrance class is added on the
        // next frame so the browser has a resting state to animate from.
        this.sticky.hidden = false;
        window.requestAnimationFrame(() => {
          if (this.stickyVisible) this.sticky.classList.add('rns-sticky-atc--in');
        });
        return;
      }

      this.sticky.classList.remove('rns-sticky-atc--in');
      // Taken out of the tab order once it has finished leaving. Guarded on the
      // current state so a fast scroll back down cannot hide a bar that has
      // since been asked to show again.
      window.setTimeout(() => {
        if (!this.stickyVisible) this.sticky.hidden = true;
      }, 400);
    }

    showMedia(mediaId) {
      if (!this.track || !mediaId) return;
      const target = this.track.querySelector(`[data-rns-media-id="${mediaId}"]`);
      if (!target) return;

      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({
        left: target.offsetLeft - this.track.offsetLeft,
        behavior: reduce ? 'auto' : 'smooth'
      });
    }

    buildItems() {
      const id = this.currentVariantId();
      if (!id) return null;

      // The quantity field is native and posts itself when scripting is off, but
      // the moment this file intercepts the submit it becomes the only thing
      // carrying it. Hardcoding 1 here was safe only while no template turned
      // the selector on; a consumable page does, and adding 1 of something the
      // customer asked 3 of is the cart-lies-about-itself failure in section 7.
      // Floor at 1: a browser that hands back an empty or unparseable value must
      // still add the item, not add nothing.
      const field = this.querySelector('[name="quantity"]');
      const parsed = field ? parseInt(field.value, 10) : 1;
      const quantity = Number.isFinite(parsed) && parsed > 0 ? parsed : 1;

      const items = [{ id: Number(id), quantity }];

      // The viewed product's own plan. This is a real product-form field, so the
      // native POST already carries it — but once this file intercepts the submit
      // to add a second line, nothing else would, and the customer would silently
      // get a one-time purchase they did not choose. Empty value means buy once.
      const ownPlan = this.querySelector('[name="selling_plan"]:checked');
      if (ownPlan && ownPlan.value) items[0].selling_plan = Number(ownPlan.value);

      const offer = this.querySelector('[name="rns_offer"]:checked');
      const tabletsId = this.dataset.rnsTabletsVariant;

      if (offer && offer.value === 'bundle' && tabletsId) {
        // The refill matches the device count. "Two bundles" means two of each;
        // two devices and one pack would be a bundle the picker did not describe.
        // Note that the admin discount rewards ONE pod however many are in the
        // cart (§0.6), so the effective rate falls as this rises — the badge is
        // still true about the amount, and understating is the safe direction.
        const refill = { id: Number(tabletsId), quantity };

        // A selling_plan turns this line into a subscription. The "buy once"
        // radio carries an empty value, which is how it opts out.
        const plan = this.querySelector('[name="rns_plan"]:checked');
        if (plan && plan.value) refill.selling_plan = Number(plan.value);

        items.push(refill);
      }

      return items;
    }

    /*
      One POST, both line items, and the cart surfaces re-rendered in the same
      response. Shopify commits an `items` array atomically, so there is no
      moment at which the pod is in the cart and the refill is not — which is
      why the event below can only ever fire after BOTH lines exist.
    */
    onSubmit(event) {
      const items = this.buildItems();
      if (!items) return; // let the native POST happen

      event.preventDefault();
      this.setBusy(true);

      const ids = cartSectionIds();
      const body = { items: items };

      if (ids.length) {
        body.sections = ids.join(',');
        body.sections_url = window.location.pathname + window.location.search;
      }

      log('submitting', items.length, 'line item(s):', items);
      log('asking for section(s):', ids.length ? ids.join(', ') : 'NONE FOUND on this page');

      fetch(root() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      })
        .then((response) => {
          if (!response.ok) throw new Error('add failed with HTTP ' + response.status);
          return response.json();
        })
        .then((data) => {
          const sections = (data && data.sections) || null;
          const keys = sections ? Object.keys(sections) : [];

          log(
            'add committed:',
            (data && data.items && data.items.length) || 0,
            'line(s). Sections returned:',
            keys.length ? keys.join(', ') : 'none'
          );

          if (ids.length && !keys.length) {
            log(
              'WARNING: sections were requested but none came back. The drawer',
              'will fall back to fetching itself. Check the section ids above.'
            );
          }

          return this.refreshCart(sections);
        })
        .then(() => {
          this.setStatus(this.dataset.rnsAdded, true);
        })
        .catch((error) => {
          // Deliberately does NOT fall back to submitting the form natively: that
          // would add the device alone while the bundle sits selected, which is
          // exactly the "toggle that lies about the cart" failure in section 7.
          log('FAILED:', (error && error.message) || error);
          this.setStatus(this.dataset.rnsError, false);
        })
        .finally(() => this.setBusy(false));
    }

    /*
      The /cart/add.js response describes the lines it added, not the cart, so
      the total count still needs its own read. That read also settles the header
      bubble in the same pass as the drawer's new markup, so the two can never
      disagree about the same add.

      The event fires either way. By the time this runs the add has already
      committed, and a drawer that never hears about a committed add is worse
      than a bubble that missed one tick.
    */
    refreshCart(sections) {
      return fetch(root() + 'cart.js', {
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      })
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null)
        .then((cart) => {
          if (cart) {
            log('cart.js reports item_count =', cart.item_count);
            this.updateCartCount(cart.item_count);
          } else {
            log('cart.js did not answer; the bubble keeps its old number');
          }

          // The cart drawer listens for this: it re-renders itself from the
          // markup attached here, and opens if the merchant has that turned on.
          // Nothing about this section depends on the drawer existing — with no
          // listener the event goes nowhere and the status message is still the
          // confirmation.
          document.dispatchEvent(
            new CustomEvent('rns:cart:updated', {
              detail: { cart: cart, sections: sections || null, source: 'product-form' }
            })
          );

          log(
            'dispatched rns:cart:updated (source: product-form) carrying',
            sections ? Object.keys(sections).length : 0,
            'pre-rendered section(s)'
          );
        });
    }

    updateCartCount(count) {
      const bubble = document.querySelector('[data-rns-cart-count]');
      if (bubble) {
        bubble.textContent = count > 99 ? '99+' : String(count);
        bubble.hidden = count === 0;
      }

      // Both plural forms come pre-translated from Liquid; swapping the digit in
      // the chosen one avoids reimplementing pluralization here.
      const text = document.querySelector('[data-rns-cart-count-text]');
      if (text) {
        const template = count === 1 ? this.dataset.rnsCountOne : this.dataset.rnsCountOther;
        if (template) text.textContent = template.replace(/\d+/, String(count));
      }
    }

    setBusy(busy) {
      const label = busy ? this.dataset.rnsAdding : this.dataset.rnsAddToCart;

      [
        [this.submit, this.submitText],
        [this.stickySubmit, this.stickySubmitText]
      ].forEach(([button, text]) => {
        if (!button) return;
        button.setAttribute('aria-busy', busy ? 'true' : 'false');
        button.disabled = busy;
        if (text) text.textContent = label;
      });
    }

    setStatus(html, success) {
      if (!this.status) return;
      this.status.innerHTML = html || '';
      // role="status" already announces it; this only marks it for styling.
      this.status.dataset.rnsState = success ? 'success' : 'error';
    }
  }

  if (!customElements.get('rns-product-form')) {
    customElements.define('rns-product-form', RnsProductForm);
  }
})();
